import { z } from 'zod';
import { MoneyCentsSchema } from './money.js';
/** The owner's existing stock code for one exact product, e.g. "KC-ORANGE". */
export const ProductCodeSchema = z
    .string()
    .trim()
    .min(1)
    .max(20)
    .regex(/^[A-Za-z0-9-]+$/)
    .transform((v) => v.toUpperCase());
export const ProductCategoryRefSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    colour: z.string().nullable(),
});
/**
 * A square crop rectangle, as fractions 0..1 of the ORIGINAL image's width
 * and height (after EXIF rotation), with (x, y) at the top-left.
 */
export const ImageCropSchema = z
    .object({
    x: z.number().finite().min(0).max(1),
    y: z.number().finite().min(0).max(1),
    w: z.number().finite().min(0.05).max(1),
    h: z.number().finite().min(0.05).max(1),
})
    .refine((v) => v.x + v.w <= 1.0001, { message: 'crop extends past the right edge of the image' })
    .refine((v) => v.y + v.h <= 1.0001, { message: 'crop extends past the bottom edge of the image' });
export const UpdateImageCropRequestSchema = z.object({
    crop: ImageCropSchema,
});
export const ProductSchema = z.object({
    id: z.number().int(),
    name: z.string(),
    /** The owner's stock code for this product, e.g. "KC-ORANGE". Unique. */
    code: z.string(),
    categoryId: z.number().int().nullable(),
    category: ProductCategoryRefSchema.nullable(),
    priceCents: MoneyCentsSchema,
    stockQty: z.number().int(),
    lowStockThreshold: z.number().int(),
    isActive: z.boolean(),
    lowStock: z.boolean(),
    /** Relative file name under `${DATA_DIR}/images/`, e.g. "12-1737100000.webp". */
    imagePath: z.string().nullable(),
    /** `/api/images/<imagePath>`, or null when no image has been uploaded. */
    imageUrl: z.string().nullable(),
    /** `/api/images/<imagePath-thumb>.webp`, or null when no image has been uploaded. */
    thumbUrl: z.string().nullable(),
    /** `/api/images/<imageOrigPath>`, or null when no image has been uploaded. */
    originalUrl: z.string().nullable(),
    /** The crop rectangle the square derived images were rendered from, or null. */
    imageCrop: ImageCropSchema.nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const CreateProductRequestSchema = z.object({
    name: z.string().trim().min(1).max(80),
    code: ProductCodeSchema,
    categoryId: z.number().int().positive().nullable().optional(),
    priceCents: z.number().int().min(0).max(9_999_999),
    stockQty: z.number().int().min(0).default(0),
    lowStockThreshold: z.number().int().min(0).default(5),
    isActive: z.boolean().default(true),
});
export const UpdateProductRequestSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    code: ProductCodeSchema.optional(),
    categoryId: z.number().int().positive().nullable().optional(),
    priceCents: z.number().int().min(0).max(9_999_999).optional(),
    lowStockThreshold: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
});
export const AdjustStockRequestSchema = z.object({
    delta: z.number().int().refine((v) => v !== 0, 'delta must not be zero'),
    reason: z.enum(['restock', 'adjustment']),
    note: z.string().trim().max(280).optional(),
});
export const ProductListResponseSchema = z.object({
    products: z.array(ProductSchema),
});
export const StockMovementSchema = z.object({
    id: z.number().int(),
    productId: z.number().int(),
    delta: z.number().int(),
    reason: z.enum(['sale', 'void', 'restock', 'adjustment']),
    refSaleId: z.number().int().nullable(),
    byUser: z.number().int(),
    /** Frozen at creation - this user's name/code AT THE TIME, not their current ones. See docs/DECISIONS.md. */
    byUserName: z.string(),
    byUserCode: z.string(),
    note: z.string().nullable(),
    createdAt: z.string(),
});
export const StockMovementListResponseSchema = z.object({
    movements: z.array(StockMovementSchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
});
//# sourceMappingURL=products.js.map